import React from "react";
import { SplideSlide } from "@splidejs/react-splide";
import classNames from "classnames";

import { AdMarqueeType, Creative } from "custom-types/AdMarquee";
import { useEventTracker } from "hooks/useEventTracker";
import isHexColorBright from "utils/isHexColorBright";
import { Action, Category } from "utils/trackEvent";

import AdFlag from "components/botanic/AdFlag";
import Image from "components/Image";
import TrackImpression from "components/TrackImpression";

import Button from "../botanic/Button";

enum Colors {
  WHITE = "#FFFFFF",
}

type Props = Partial<Creative> & {
  retailerName: string | null;
  trackingCategory: Category;
  slot: number;
};

const AdMarqueeCard = ({
  backgroundColor,
  headline,
  destinationUrl,
  imageUrl,
  retailerName,
  creativeType,
  textColor,
  buttonText,
  buttonColor,
  buttonTextColor,
  merchandisingCampaignId,
  dispensaryId,
  trackingCategory,
  slot,
}: Props) => {
  const brightnessCheck =
    backgroundColor && isHexColorBright(backgroundColor, 0.85);
  const cardClassNames = classNames(
    "ad-marquee-card rounded-t rounded-b pb-2",
    { "border border-light-grey": brightnessCheck }
  );
  const imageClassNames = classNames(
    "image absolute md:rounded-tr rounded-br overflow-hidden bottom-0 right-0 w-1/4",
    { "allow-border": brightnessCheck }
  );
  const textStyle = {
    color: textColor || Colors.WHITE,
  };
  const { publishEvent } = useEventTracker();
  const isDispensaryAd = creativeType === AdMarqueeType.DISPENSARY;

  return (
    <>
      <SplideSlide>
        <TrackImpression
          className="w-full"
          trackingFunction={() => {
            publishEvent({
              action: Action.impression,
              ...(isDispensaryAd && {
                dispensaryId,
                merchandisingCampaignId,
              }),
              category: trackingCategory,
              label: `slot #${slot + 1}`,
            });
          }}
        >
          <div className={cardClassNames}>
            <a
              href={destinationUrl}
              rel={isDispensaryAd ? "sponsored" : ""}
              onClick={() => {
                publishEvent({
                  action: Action.click,
                  ...(isDispensaryAd && {
                    dispensaryId,
                    merchandisingCampaignId,
                  }),
                  category: trackingCategory,
                  label: `slot #${slot + 1}`,
                });
              }}
            >
              <div className="pl-4 md:pl-16 lg:pl-20 font-extrabold h-32 flex flex-col justify-end">
                <p className="text-xs md:w-1/2" style={textStyle}>
                  {retailerName}
                </p>
                <p className="text-md pb-4 marquee-text" style={textStyle}>
                  {headline}
                </p>
              </div>
              <div className="absolute sm:bottom-14 md:bottom-12 pl-4 md:pl-16 lg:pl-20 w-1/2 md:w-1/3">
                <Button
                  className="text-xs"
                  style={{
                    backgroundColor: buttonColor,
                    color: buttonTextColor,
                  }}
                >
                  {buttonText || "Shop Now"}
                </Button>
              </div>

              {imageUrl && (
                <div className={imageClassNames} role="presentation">
                  <Image
                    src={imageUrl}
                    alt={`${creativeType} ad`}
                    sizes={[320]}
                  />
                </div>
              )}
              <AdFlag promoted={isDispensaryAd} position={"right"} />
            </a>
          </div>
        </TrackImpression>
      </SplideSlide>
      <style jsx>{`
        .ad-marquee-card {
          background-color: ${backgroundColor};
          position: relative;
          height: 215px;
        }
        .ad-marquee-card .slide_pagination_page .is_active {
          transform: scale(0);
        }
        @media screen and (max-width: 430px) {
          .image {
            height: 100px;
            width: 118px;
          }
        }
        @media screen and (min-width: 768px) {
          .image {
            height: 215px;
            width: 254px;
          }
          .image.allow-border {
            height: 213px;
            width: 252px;
          }
          .marquee-text {
            width: calc(100% - 254px);
          }
        }
      `}</style>
    </>
  );
};

export default AdMarqueeCard;
